import debounce from 'lodash/debounce';
import React, { useCallback, useRef } from 'react';

import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import { Coords, ElementAddress, MediaText } from 'editor/src/store/design/types';
import { isNonFiniteNumber } from 'editor/src/store/design/util/dropNonFiniteGeometry';
import { useDispatch } from 'editor/src/store/hooks';

import FabricPathText from 'editor/src/fabric/FabricPathText';
import { captureMessage } from 'editor/src/logger';
import limitPrecision from 'editor/src/util/limitPrecision';
import mm2pt from 'editor/src/util/mm2pt';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import { CanvasRotation } from 'editor/src/component/EditorArea/types';
import { POINT_0_0 } from 'editor/src/component/EditorArea/useCanvasRotation';

import getElementRect from '../getElementRect';

import fabricPropsToTextElementData from './fabricPropsToTextElementData';
import getCustomFontContentWidth from './getCustomFontContentWidth';
import { extractTextProperties } from './updateTextElementWithoutRender';

const GEOMETRY_KEYS = ['x', 'y', 'width', 'height', 'r'] as const;

// px -> mm -> px round trips leave float noise far below anything visible
const isSameNumber = (a: number | undefined, b: number | undefined) =>
  a === b || (a !== undefined && b !== undefined && limitPrecision(a, 6) === limitPrecision(b, 6));

function useTextUpdates(
  pageCoords: Coords,
  elementData: MediaText,
  elementAddress: ElementAddress,
  fabricElementRef: React.RefObject<FabricPathText>,
  canvasRotation: CanvasRotation,
  loadedFontFamily: string,
) {
  const dispatch = useDispatch();
  const { px2mm, mm2px } = useFabricUtils();

  const elementDataRef = useRef(elementData);
  elementDataRef.current = elementData;

  const updateText = useCallback(
    debounce((textUpdates: Partial<MediaText>) => {
      dispatch(updateMediaElementOperation(elementAddress, textUpdates, MediaUpdateActionName.TEXT_UPDATED));
    }, 200),
    [px2mm, pageCoords, elementAddress, canvasRotation],
  );

  const onTextChanged = useCallback(() => {
    const elt = fabricElementRef.current;
    if (!elt) {
      return;
    }
    const textUpdate = fabricPropsToTextElementData(px2mm, pageCoords, elt, canvasRotation);
    textUpdate.extra = {
      ...elementDataRef.current.extra,
      text: elt.text,
      fontSize: mm2pt(px2mm(elt.fontSize ?? 1)),
    };
    // Clear stale SVG path when text changes on custom fonts — will be regenerated
    if (elementDataRef.current.extra?.fontFamily?.startsWith('custom-')) {
      textUpdate.path = undefined;
    }

    const { fontFamily } = elementDataRef.current.extra;
    // A custom font only counts as "custom" for measurement if it actually
    // loaded. When it fails to load, useFontLoader falls back to the default
    // family (loadedFontFamily) for rendering — so we must measure with that
    // same fallback, otherwise calcTextWidth collapses to ~0 and the box goes
    // vertical.
    const isLoadedCustomFont = !!fontFamily?.startsWith('custom-') && loadedFontFamily === fontFamily;

    if (elementDataRef.current.extra.flexibleWidth && isLoadedCustomFont) {
      // Loaded custom (imported) font: extractTextProperties can't re-measure it
      // — it builds a throwaway FabricPathText without the loaded custom font, so
      // calcTextWidth() collapses to ~0. The live `elt` HAS the font loaded, so
      // read the visible glyph extent straight off it.
      const contentWidth = getCustomFontContentWidth(elt, px2mm);
      // Use the measured glyph width when the font is loaded and measurable;
      // otherwise keep the element's existing width (the imported path-visual
      // width) rather than the wide source frame fabricPropsToTextElementData
      // returns — and never let it collapse to a sliver.
      textUpdate.width = contentWidth ?? elementDataRef.current.width;
      textUpdate.x = elementDataRef.current.x;
      textUpdate.y = elementDataRef.current.y;
    } else if (elementDataRef.current.extra.flexibleWidth) {
      // Normal fonts, and custom fonts that failed to load (rendered with the
      // fallback default font): measure with the effective loaded family so the
      // throwaway FabricPathText has a real, loaded font and returns a real width.
      const merged = {
        ...elementDataRef.current,
        ...textUpdate,
        extra: { ...elementDataRef.current.extra, ...textUpdate.extra, fontFamily: loadedFontFamily },
      };
      const props = extractTextProperties(merged, undefined);
      textUpdate.width = props.width;
      textUpdate.height = props.height;
      textUpdate.x = elementDataRef.current.x;
      textUpdate.y = elementDataRef.current.y;

      const targetRect = getElementRect(elementDataRef.current, pageCoords, canvasRotation, mm2px);
      elt.left = targetRect.left;
      elt.top = targetRect.top;
      elt.setCoords();
    }

    updateText(textUpdate);
  }, [updateText, mm2px, px2mm, pageCoords, canvasRotation, loadedFontFamily]);

  const onObjectModified = useCallback(() => {
    if (fabricElementRef.current) {
      const textUpdate = fabricPropsToTextElementData(px2mm, pageCoords, fabricElementRef.current, canvasRotation);
      if (fabricElementRef.current.group) {
        // in a multi-selection fabric's left/top are relative to the selection, and only moving the
        // selection moves the text (useMoveGroupSelection stores that), so the position stays as stored
        textUpdate.x = elementDataRef.current.x;
        textUpdate.y = elementDataRef.current.y;
        textUpdate.r = elementDataRef.current.r;
      }
      if ([textUpdate.x, textUpdate.y, textUpdate.width, textUpdate.height, textUpdate.r].some(isNonFiniteNumber)) {
        // The position can't be stored, so put the text back where the design
        // has it: what the customer sees must be what gets printed.
        const elt = fabricElementRef.current;
        let targetRect = getElementRect(elementDataRef.current, pageCoords, canvasRotation, mm2px);
        if (!Number.isFinite(targetRect.left) && canvasRotation.angleRad === 0) {
          // Without canvas rotation the pivot does not affect the result, so a
          // broken pivot must not stop the text from going back.
          targetRect = getElementRect(
            elementDataRef.current,
            pageCoords,
            { ...canvasRotation, canvasCenter: POINT_0_0 },
            mm2px,
          );
        }
        if (Number.isFinite(targetRect.left) && Number.isFinite(targetRect.top)) {
          elt.set({ left: targetRect.left, top: targetRect.top, angle: targetRect.angle });
          elt.setCoords();
          elt.canvas?.requestRenderAll();
        }
        captureMessage('Text move produced non-finite geometry, reverted', {
          extra: { canvasCenter: canvasRotation.canvasCenter, left: elt.left, top: elt.top },
        });
        return;
      }
      const fontSize = fabricElementRef.current.fontSize
        ? mm2pt(px2mm(fabricElementRef.current.fontSize))
        : elementDataRef.current.extra.fontSize;
      // a modified that neither moves nor resizes the text (a fixed frame turned on, frames linked) would
      // otherwise add an undo step that changes nothing
      if (
        isSameNumber(fontSize, elementDataRef.current.extra.fontSize) &&
        GEOMETRY_KEYS.every((key) => isSameNumber(textUpdate[key], elementDataRef.current[key]))
      ) {
        return;
      }
      if (fabricElementRef.current.fontSize) {
        textUpdate.extra = { ...elementDataRef.current.extra, fontSize };
      }
      dispatch(updateMediaElementOperation(elementAddress, textUpdate, MediaUpdateActionName.MOVED));
    }
  }, [px2mm, mm2px, pageCoords, elementAddress, canvasRotation]);

  const flushTextUpdates = useCallback(() => {
    updateText.flush();
  }, [updateText]);

  return { onObjectModified, onTextChanged, flushTextUpdates };
}

export default useTextUpdates;
